/*  MUI  */
import {
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText
} from '@mui/material';

/*  Shared  */
import { Permission } from 'application/Shared/Components/Core';

/*  React  */
import React from 'react';
import { Link } from 'react-router';

/**
 * Renders a single sidebar list item with icon and text
 * @param {Object} props - Component props
 * @param {boolean} props.bottomItem - Whether this item appears at the bottom of the sidebar
 * @param {JSX.Element} props.icon - Icon element to display
 * @param {string} props.id - In case name is a jsx element, we need an id to add as data-trackid
 * @param {string| JSX.Element} props.name - Text label for the list item
 * @param {Function} [props.onClick] - Optional click handler
 * @param {string} props.route - Route path for navigation
 * @param {boolean} props.selected - Whether this item is currently selected
 * @returns {JSX.Element} Rendered list item component
 */
const RenderItem = ({
    bottomItem,
    icon,
    id,
    name,
    onClick,
    route,
    selected
}: {
    bottomItem: boolean;
    icon: JSX.Element;
    id?: string;
    name: string | JSX.Element;
    onClick?: () => void;
    route: string;
    selected: boolean;
}): JSX.Element => (
    <Link data-testid="sidebarRenderItem" to={route}>
        <ListItemButton
            data-trackid={`sidebarRenderItemButton_${id || name}`}
            data-testid={`sidebarRenderItemButton_${id || name}`}
            onClick={
                onClick
                    ? () => {
                          onClick();
                      }
                    : undefined
            }
            selected={selected}
            sx={{
                '&.Mui-selected': {
                    backgroundColor: 'sidebar.itemSelected.backgroundColor'
                },
                '&.Mui-selected:hover': {
                    backgroundColor:
                        'sidebar.itemSelected.hover.backgroundColor'
                },
                '&:hover': {
                    backgroundColor: bottomItem
                        ? 'sidebar.item.bottomItemHover.backgroundColor'
                        : 'sidebar.item.hover.backgroundColor'
                },
                padding: '4px 16px 4px 8px'
            }}
        >
            <ListItem sx={{ padding: 0 }}>
                <ListItemIcon
                    style={{
                        minWidth: '36px',
                        paddingLeft: '10px',
                        paddingRight: '5px'
                    }}
                >
                    {icon}
                </ListItemIcon>
                <ListItemText sx={{ color: 'sidebar.text' }}>
                    {name}
                </ListItemText>
            </ListItem>
        </ListItemButton>
    </Link>
);

/**
 * A component that renders a list item in the sidebar with optional permission checking
 * @param {Object} props - Component props
 * @param {boolean} [props.bottomItem] - Flag indicating if this is a bottom item in the sidebar
 * @param {JSX.Element} props.icon - Icon component or element to display
 * @param {string} props.id - In case name is a jsx element, we need an id to add as data-trackid
 * @param {string | JSX.Element} props.name - Text label to display for the item
 * @param {Function} [props.onClick] - Optional click handler function
 * @param {string | string[]} [props.permission] - Array of required permissions to display this item
 * @param {('and'|'or')} [props.permissionOperator] - Operator to use when checking multiple permissions
 * @param {string} props.route - Route path for the Link component
 * @param {boolean} [props.selected] - Flag indicating if this item is currently selected
 * @returns {JSX.Element} Rendered sidebar list item with optional permission wrapper
 */
export const SidebarListItem = ({
    bottomItem = false,
    icon,
    id,
    name,
    onClick,
    permission,
    permissionOperator = 'and',
    route,
    selected = false
}: {
    bottomItem?: boolean;
    icon: JSX.Element;
    id?: string;
    name: string | JSX.Element;
    onClick?: () => void;
    permission?: string | string[];
    permissionOperator?: 'and' | 'or';
    route: string;
    selected?: boolean;
}): JSX.Element => {
    if (permission) {
        return (
            <Permission required={permission} operator={permissionOperator}>
                <RenderItem
                    bottomItem={bottomItem}
                    icon={icon}
                    id={id}
                    name={name}
                    onClick={onClick}
                    route={route}
                    selected={selected}
                />
            </Permission>
        );
    }

    return (
        <RenderItem
            bottomItem={bottomItem}
            icon={icon}
            id={id}
            name={name}
            onClick={onClick}
            route={route}
            selected={selected}
        />
    );
};
